import { useState } from "react";
import {
  Page,
  ItemTable,
  ItemTableBody,
  ItemTableRow,
  ItemTableCell,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTablePagination,
  ItemTableLoading,
  ItemTableEmpty,
  Badge,
} from "@ui";
import { ScrollText, Wrench, Eye } from "lucide-react";
import { useLogs, useLogsStats, type LogMessage } from "@agentic_system/api/logs";
import { ConversationSheet } from "./conversation-sheet";

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

function formatCost(cost: number | string | null): string {
  const n = Number(cost) || 0;
  if (n === 0) return "$0";
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function formatDate(date: string): string {
  return new Date(date).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <div className="text-sm text-slate-500 mb-1">{label}</div>
      <div className="text-2xl font-semibold text-slate-800">{value}</div>
    </div>
  );
}

export function Logs() {
  const [page, setPage] = useState(1);
  const [selectedConversationId, setSelectedConversationId] = useState<number | null>(null);
  const { data, isLoading, error } = useLogs(page);
  const { data: stats } = useLogsStats();

  const handleRowClick = (message: LogMessage) => {
    setSelectedConversationId(message.conversation.id);
  };

  if (error) {
    return (
      <Page
        data-testid="logs"
        title="Journaux"
        description="Voir toutes les requêtes et coûts LLM"
      >
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Impossible de charger les journaux. Veuillez réessayer.
        </div>
      </Page>
    );
  }

  const messages = data?.messages ?? [];
  const meta = data?.meta;

  return (
    <Page
      data-testid="logs"
      title="Journaux"
      description="Voir toutes les requêtes et coûts LLM"
    >
      {/* Stats cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Tokens d'entrée"
          value={stats ? formatNumber(stats.total_input_tokens) : "—"}
        />
        <StatCard
          label="Tokens de sortie"
          value={stats ? formatNumber(stats.total_output_tokens) : "—"}
        />
        <StatCard
          label="Coût total"
          value={stats ? formatCost(stats.total_cost_usd) : "—"}
        />
      </div>

      {isLoading ? (
        <ItemTable>
          <ItemTableLoading rows={10} />
        </ItemTable>
      ) : messages.length === 0 ? (
        <ItemTable>
          <ItemTableEmpty
            icon={ScrollText}
            title="Aucun journal"
            description="Les messages apparaîtront ici une fois les conversations démarrées."
          />
        </ItemTable>
      ) : (
        <ItemTable>
          <ItemTableBody>
            {messages.map((msg) => (
              <ItemTableRow
                key={msg.id}
                onClick={() => handleRowClick(msg)}
                className="grid-cols-[1fr_auto_auto_auto_auto_auto_auto] gap-x-4 cursor-pointer"
              >
                <ItemTableCell>
                  <div className="min-w-0">
                    <ItemTableTitle className="truncate">
                      {msg.content || msg.conversation.title || "Sans titre"}
                    </ItemTableTitle>
                    <ItemTableSubtitle>
                      {msg.user.name || msg.user.email}
                      {msg.user.profile_type && ` - ${msg.user.profile_type}`}
                      {msg.user.profile_type !== "candidat" && msg.user.profile_label && ` - ${msg.user.profile_label}`}
                    </ItemTableSubtitle>
                  </div>
                </ItemTableCell>
                <ItemTableCell>
                  {msg.tool_count > 0 && (
                    <Badge
                      label={`${msg.tool_count} outil${msg.tool_count > 1 ? "s" : ""}`}
                      color="purple"
                      size="sm"
                      iconLeft={Wrench}
                    />
                  )}
                </ItemTableCell>
                <ItemTableCell>
                  <Badge
                    label={`in ${formatNumber(msg.input_tokens)}`}
                    color="default"
                    size="sm"
                  />
                </ItemTableCell>
                <ItemTableCell>
                  <Badge
                    label={`out ${formatNumber(msg.output_tokens)}`}
                    color="default"
                    size="sm"
                  />
                </ItemTableCell>
                <ItemTableCell>
                  <Badge
                    label={formatCost(msg.cost_usd)}
                    color="default"
                    size="sm"
                  />
                </ItemTableCell>
                <ItemTableCell>
                  <Badge
                    label={formatDate(msg.created_at)}
                    color="default"
                    size="sm"
                  />
                </ItemTableCell>
                <ItemTableCell>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRowClick(msg);
                    }}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </ItemTableCell>
              </ItemTableRow>
            ))}
          </ItemTableBody>
          {meta && meta.total_pages > 1 && (
            <ItemTablePagination meta={meta} onPageChange={setPage} />
          )}
        </ItemTable>
      )}

      <ConversationSheet
        conversationId={selectedConversationId}
        onClose={() => setSelectedConversationId(null)}
      />
    </Page>
  );
}

export default Logs;
